import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { BaselineCheckActions } from './baseline-check.actions';
import { BaselineCheckResponse } from '../../../../graphql/generated';
import { convertListoToOptions } from '../../shared/ra.operations';
import { FilterMetadata } from 'primeng/api';

export const baselineChecksFeatureKey = 'baselineChecks';

export interface State extends EntityState<BaselineCheckResponse> {
  loading: boolean;
  filters: any;
  loadingRows: Record<string, string[]>;
}

export const adapter: EntityAdapter<BaselineCheckResponse> = createEntityAdapter<BaselineCheckResponse>({
  selectId: (baselineCheck: BaselineCheckResponse) => baselineCheck.cptCatalogue!.id!
});

export const initialState: State = adapter.getInitialState({
  loading: false,
  filters: {},
  loadingRows: {}
});

export const reducer = createReducer(
  initialState,

  on(BaselineCheckActions.addBaselineCheck,
    (state, action) => adapter.addOne(action.baselineCheck, state)
  ),

  on(BaselineCheckActions.upsertBaselineCheck,
    (state, action) => adapter.upsertOne(action.baselineCheck, state)
  ),

  on(BaselineCheckActions.addBaselineChecks,
    (state, action) => adapter.addMany(action.baselineChecks, state)
  ),

  on(BaselineCheckActions.upsertBaselineChecks,
    (state, action) => adapter.upsertMany(action.baselineChecks, state)
  ),

  on(BaselineCheckActions.deleteBaselineCheck,
    (state, action) => adapter.removeOne(action.id, state)
  ),

  on(BaselineCheckActions.deleteBaselineChecks,
    (state, action) => adapter.removeMany(action.ids, state)
  ),

  on(BaselineCheckActions.loadBaselineCheck,
    (state) => ({
      ...state,
      loading: true
    })
  ),

  on(BaselineCheckActions.loadBaselineCheckSuccess, (state, action) => ({
    ...adapter.setAll(action.baselineCheck, state),
    loading: false,
    filters: buildGenericFilters(action.baselineCheck as BaselineCheckResponse[])
  })),

  on(BaselineCheckActions.loadBaselineCheckFailure, (state, action) => ({
    ...state,
    loading: false
  })),

  on(BaselineCheckActions.clearBaselineChecks,
    state => adapter.removeAll(state)
  ),

  on(BaselineCheckActions.updateBaselineCheck,
    (state, action) => ({
      ...state,
      // loading: true,
      loadingRows: {
        ...state.loadingRows,
        [action.selection]: [
          ...(state.loadingRows?.[action.selection] ?? []),
          action.id
        ]
      }
    })
  ),

  on(BaselineCheckActions.updateBaselineCheckSuccess, (state, action) => {
    const updatedState = adapter.updateOne(
      { id: action.id, changes: { icsRationale: action.icsRationale, irRationale: action.irRationale } },
      state
    );

    return {
      ...updatedState,
      filters: buildGenericFilters(updatedState.ids.map(id => updatedState.entities[id]) as BaselineCheckResponse[]),
      loadingRows: {
        ...updatedState.loadingRows,
        [action.selection]: updatedState.loadingRows[action.selection].filter(r => r !== action.cptId)
      }
    };
  }),

  on(BaselineCheckActions.updateBaselineCheckFailure, (state, action) => ({
    ...state,
    // loading: false,
    loadingRows: {
      ...state.loadingRows,
      [action.selection]: state.loadingRows[action.selection].filter(r => r !== action.id)
    }
  })),

  on(BaselineCheckActions.createBaselineCheck,
    (state, action) => ({
      ...state,
      // loading: true,
      loadingRows: {
        ...state.loadingRows,
        [action.selection]: [
          ...(state.loadingRows?.[action.selection] ?? []),
          action.cptId
        ]
      }
    })
  ),

  on(BaselineCheckActions.createBaselineCheckSuccess, (state, action) => ({
    ...state,
    ...adapter.updateOne({ id: action.id, changes: { icsRationale: action.icsRationale, irRationale: action.irRationale } }, state),
    loading: false,
    loadingRows: {
      ...state.loadingRows,
      [action.selection]: state.loadingRows[action.selection].filter(r => r !== action.cptId)
    }
  })),

  on(BaselineCheckActions.createBaselineCheckFailure, (state, action) => ({
    ...state,
    loading: false,
    loadingRows: {
      ...state.loadingRows,
      [action.selection]: state.loadingRows[action.selection].filter(r => r !== action.id)
    }
  }))
);

export const baselineChecksFeature = createFeature({
  name: baselineChecksFeatureKey,
  reducer,
  extraSelectors: ({
    selectIds,
    selectEntities,
    selectFilters
  }) => ({
    selectBaselineChecksList: (
      filter: (string | boolean)[],
      column: string,
      filterField?: string,
      filterValue?: { [s: string]: FilterMetadata | FilterMetadata[] }
    ) => createSelector(
      selectEntities,
      selectIds,
      (entities, ids) => {
        let entityList = entities ? ids.map<BaselineCheckResponse>((id) => entities[id] as BaselineCheckResponse) : [];
        const actual = column.toLowerCase() + 'Actual';
        const baseline = column.toLowerCase() + 'Baseline';

        // Filtro basato sul confronto tra actual e baseline
        if (filter?.length === 1) {
          entityList = entityList.filter((entity: any) =>
            (filter[0] == 'Match' && (entity[actual] === entity[baseline] || !entity[actual] || !entity[baseline])) ||
            (filter[0] == 'No Match' && (entity[actual] !== entity[baseline] && entity[actual] && entity[baseline]))
          );
        }

        // Gestione di filterValue
        if (filterValue) {
          Object.entries(filterValue).forEach(([field, fieldFilter]) => {
            const selectedValues = Array.isArray(fieldFilter)
              ? fieldFilter.flatMap(f => f.value) // Unisci valori se ci sono più filtri
              : fieldFilter.value; // Singolo filtro

            if (Array.isArray(selectedValues)) {
              // Filtra se il campo è presente in uno dei valori selezionati
              entityList = entityList.filter((entity: any) =>
                selectedValues.includes(entity[field])
              );
            }
          });
        }

        return entityList;
      }
    ),

    selectNames: (fieldName: string) =>
      createSelector(selectFilters, (filters) => {
        if (fieldName === 'x')
          return ['Match', 'No match']
        else
          return convertListoToOptions(filters?.[fieldName] || [])
      }
      )
  })
});


export const {
  selectIds,
  selectEntities,
  selectBaselineChecksList,
  selectLoading,
  selectNames,
  selectFilters,
  selectLoadingRows
} = baselineChecksFeature;


function buildGenericFilters(entity: BaselineCheckResponse[]) {
  return {
    'levelTwoProcessCode': entity.map(e => e.cptCatalogue?.levelTwoProcessCode) as string[] || [],
    'levelTwoProcessName': entity.map(e => e.cptCatalogue?.levelTwoProcessName) as string[] || [],
    'irActual': entity.map(e => e.irActual) as string[] || [],
    'icsActual': entity.map(e => e.icsActual) as string[] || [],
    'irBaseline': entity.map(e => e.irBaseline) as string[] || [],
    'icsBaseline': entity.map(e => e.icsBaseline) as string[] || [],
    'irRationale': entity.map(e => e.irRationale) as string[] || [],
    'icsRationale': entity.map(e => e.icsRationale) as string[] || []
  }

}